import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, SparklesIcon } from 'lucide-react';
import type { Chapter } from '../types/pulse';
interface ChapterOpeningProps {
  chapter: Chapter;
  onBegin: () => void;
}
const ease = [0.22, 1, 0.36, 1] as const;
export function ChapterOpening({
  chapter,
  onBegin
}: ChapterOpeningProps) {
  const {
    openingLayout: layout,
    accent
  } = chapter;
  const text = <>
      <motion.p initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 0.2
    }} className="text-[11px] font-semibold uppercase tracking-[0.22em]" style={{
      color: accent
    }} data-id="element-372" data-mp="26yikiz34r7g2">
        Chapter {chapter.numberWord} · {chapter.theme}
      </motion.p>
      <motion.h1 initial={{
      opacity: 0,
      y: 16
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.7,
      delay: 0.3,
      ease
    }} className="mt-2 font-serif text-[38px] font-normal leading-[1.02] tracking-tight text-navy-900" data-id="element-373" data-mp="22y8c7frv84y1">
        {chapter.title}
      </motion.h1>
      <motion.p initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.7,
      delay: 0.5,
      ease
    }} className="mt-3 font-serif text-[18px] font-light italic leading-snug text-slate-600" data-id="element-374" data-mp="28hwwubzdjdau">
        “{chapter.openingThought}”
      </motion.p>
      <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.6,
      delay: 0.75,
      ease
    }} className="mt-5 flex items-start gap-3 rounded-2xl p-4" style={{
      backgroundColor: `${accent}12`
    }} data-id="element-375" data-mp="2djmc63tmqd4q">
        <SparklesIcon className="mt-0.5 h-4 w-4 shrink-0" style={{
        color: accent
      }} data-id="element-376" data-mp="1ymdbukmclz0v" />
        <p className="text-[14px] font-medium leading-snug text-navy-900" data-id="element-377" data-mp="1ttsl5ryqo21k">{chapter.promise}</p>
      </motion.div>
      <motion.button type="button" onClick={onBegin} initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.6,
      delay: 0.9,
      ease
    }} className="mt-5 flex h-14 w-full items-center justify-center gap-2 rounded-full bg-navy-900 text-[15px] font-semibold text-white transition hover:bg-navy-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2" data-id="element-378" data-mp="163py5q9olm9t">
        Find out
        <ArrowRightIcon className="h-4 w-4" style={{
        color: accent
      }} data-id="element-379" data-mp="3fxv6mq2m7gxi" />
      </motion.button>
    </>;
  const bgImage = <motion.img src={chapter.image} alt="" initial={{
    scale: 1.2
  }} animate={{
    scale: 1.04
  }} transition={{
    duration: 10,
    ease: 'easeOut'
  }} className="absolute inset-0 h-full w-full object-cover" data-id="element-380" data-mp="2iav4n5t8sbfo" />;
  const bigNumber = <motion.span aria-hidden="true" initial={{
    opacity: 0,
    x: -20
  }} animate={{
    opacity: 1,
    x: 0
  }} transition={{
    duration: 1,
    delay: 0.1,
    ease
  }} className="font-serif text-[110px] font-light leading-none" style={{
    color: 'transparent',
    WebkitTextStroke: `1.5px ${accent}`
  }} data-id="element-381" data-mp="2x7dhevgzr3gw">
      {chapter.number}
    </motion.span>;
  if (layout === 'full') {
    return <div className="relative flex h-full flex-col justify-end overflow-hidden bg-mist-50" data-id="element-382" data-mp="1rzaxfx4xv4zd">
        {bgImage}
        <div className="absolute inset-0 bg-white/40" data-id="element-383" data-mp="1n7swh92x14ee" />
        <div className="relative m-3 rounded-[28px] bg-white/90 px-5 pb-5 pt-4 backdrop-blur-xl" data-id="element-384" data-mp="1i9rdsp9ej73g">
          <div className="-mb-2" data-id="element-385" data-mp="1daojnzuymla9">{bigNumber}</div>
          {text}
        </div>
      </div>;
  }
  if (layout === 'framed') {
    return <div className="relative flex h-full flex-col overflow-y-auto bg-mist-50 px-6 pb-7 pt-24 no-scrollbar md:pt-28" data-id="element-386" data-mp="19806xh34ku80">
        <motion.div initial={{
        opacity: 0,
        scale: 0.92
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 1,
        ease
      }} className="relative mx-auto aspect-square w-[68%] shrink-0 overflow-hidden rounded-full border-4 bg-white" style={{
        borderColor: `${accent}30`
      }} data-id="element-387" data-mp="14ai7chazzrco">
          {bgImage}
          <span className="absolute inset-0 flex items-center justify-center font-serif text-[60px] font-light" style={{
          color: accent
        }} aria-hidden="true" data-id="element-388" data-mp="1rayzyt8robfl">
            {chapter.number}
          </span>
        </motion.div>
        <div className="mt-auto pt-6" data-id="element-389" data-mp="1hfoufvj5tlx2">{text}</div>
      </div>;
  }
  return <div className="relative flex h-full flex-col overflow-y-auto bg-white no-scrollbar" data-id="element-390" data-mp="1makrxb7611hk">
      <div className="relative h-[40%] min-h-[250px] shrink-0 overflow-hidden bg-mist-100" data-id="element-391" data-mp="17m82kx2l2gw1">
        {bgImage}
        <div className="absolute -bottom-3 left-5" data-id="element-392" data-mp="1cgc9xhfrgqxn">{bigNumber}</div>
      </div>
      <div className="flex flex-1 flex-col justify-end px-6 pb-7 pt-5" data-id="element-393" data-mp="yoe7fpgk1tag">{text}</div>
    </div>;
}